import { get as getNested, useDebouncedCallback } from '@vtex/admin-ui'
import { createContext, useCallback, useContext, useState } from 'react'
import { toLoggableError } from 'shared'
import type { DefaultProps, LogType } from 'typings'
import { useLogger } from './useLogger'

/* 
  Use this to invalidate previously stored cache values.
  This is useful when changing the way something is stored, 
  causing a breaking change with older versions' values. 
  Just bump this value and you'll be good to go.
*/
const CACHE_VERSION = 0

const ONE_SECOND = 1000

interface LocalStoragePersistenceType {
  get: (key: string, defaultValue?: any, storage?: STORAGE) => any
  set: (key: string, value: any, storage?: STORAGE) => void
}

export enum STORAGE {
  LOCAL = 'localStorage',
  SESSION = 'sessionStorage',
}

export const LocalStoragePersistence = createContext<
  LocalStoragePersistenceType | undefined
>(undefined)

function restoreCache(
  key: string,
  storage: STORAGE = STORAGE.SESSION,
  logger: (args: LogType) => void
) {
  try {
    const data = JSON.parse(window[storage].getItem(key) ?? '{}')

    if (data['__version'] !== CACHE_VERSION) {
      throw new Error('INVALID_VERSION')
    }

    return data
  } catch (error) {
    try {
      window[storage].removeItem(key)
    } catch (e) {
      logger({
        level: 'warn',
        message: `Failed to persist ${storage} cache for key ${key}`,
        type: 'catch',
        metadata: {
          id: 'cache-restore-cleanup',
          cause: toLoggableError(e),
          payload: { key },
        },
      })
    }

    return { __version: CACHE_VERSION }
  }
}

interface LocalStoragePersistenceProviderProps extends DefaultProps {
  cacheKey: string
}

export function LocalStoragePersistenceProvider(
  props: LocalStoragePersistenceProviderProps
) {
  const { cacheKey, children } = props

  const { logger } = useLogger()

  const [localCache, setLocalCache] = useState<Record<string, any>>(() =>
    restoreCache(cacheKey, STORAGE.LOCAL, logger)
  )
  const [sessionCache, setSessionCache] = useState<Record<string, any>>(() =>
    restoreCache(cacheKey, STORAGE.SESSION, logger)
  )

  const persistSession = useDebouncedCallback(
    (values) => {
      try {
        window.sessionStorage.setItem(cacheKey, JSON.stringify(values))
      } catch (error) {
        logger({
          level: 'warn',
          message: `Failed to persist session cache for key ${cacheKey}`,
          type: 'catch',
          metadata: {
            id: 'cache-persist-session',
            cause: toLoggableError(error),
            payload: { cacheKey },
          },
        })
      }
    },
    1000,
    { maxWait: 4000 }
  )

  const persistLocal = useDebouncedCallback(
    (values) => {
      try {
        window.localStorage.setItem(cacheKey, JSON.stringify(values))
      } catch (error) {
        logger({
          level: 'warn',
          message: `Failed to persist local cache for key ${cacheKey}`,
          type: 'catch',
          metadata: {
            id: 'cache-persist-local',
            cause: toLoggableError(error),
            payload: { cacheKey },
          },
        })
      }
    },
    ONE_SECOND,
    { maxWait: 4 * ONE_SECOND }
  )

  const set = useCallback(
    (key: string, value: any, storage: STORAGE = STORAGE.SESSION) => {
      const setter =
        storage === STORAGE.SESSION ? setSessionCache : setLocalCache
      setter((c) => {
        const updated = { ...c, [key]: value }

        storage === STORAGE.SESSION
          ? persistSession(updated)
          : persistLocal(updated)
        return updated
      })
    },
    [persistLocal, persistSession]
  )

  const get = useCallback(
    (key: string, defaultValue?: any, storage: STORAGE = STORAGE.SESSION) => {
      const cache = storage === STORAGE.SESSION ? sessionCache : localCache
      return getNested(cache, key) ?? defaultValue
    },
    [localCache, sessionCache]
  )

  return (
    <LocalStoragePersistence.Provider value={{ get, set }}>
      {children}
    </LocalStoragePersistence.Provider>
  )
}

export function useCache() {
  const context = useContext(LocalStoragePersistence)

  if (context === undefined) {
    throw new Error(
      'LocalStoragePersistenceProvider must be in the component tree in order to use useCache hook'
    )
  }

  return context
}
